import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";

const PACKAGES = [
  {
    name: "Trekking & Hiking",
    desc: "Classic Himalayan treks including Everest Base Camp, Annapurna Circuit, and hidden valley adventures.",
    icon: "Mountain",
    count: "16+ Treks",
    path: "/trek-discovery-hub",
    image: "https://upload.wikimedia.org/wikipedia/commons/5/5b/Mt._Fishtail_and_trekkers.jpg",
    color: "from-emerald-500 to-emerald-600",
  },
  {
    name: "Pilgrimage Tours",
    desc: "Sacred journeys to Lumbini, Muktinath, and ancient Buddhist and Hindu pilgrimage sites.",
    icon: "Compass",
    count: "4 Tours",
    path: "/activities?category=Cultural+Tour",
    image: "https://upload.wikimedia.org/wikipedia/commons/4/4b/The_Pashupatinath_Temple_27.jpg",
    color: "from-amber-500 to-amber-600",
  },
  {
    name: "Heritage Sites",
    desc: "Explore Nepal's UNESCO World Heritage Sites — ancient durbar squares, stupas, and temples.",
    icon: "Landmark",
    count: "10 Sites",
    path: "/activities/world-heritage-sites",
    image: "https://upload.wikimedia.org/wikipedia/commons/e/e0/Boudhanath_stupa%2C_Kathmandu_01.jpg",
    color: "from-purple-500 to-purple-600",
  },
  {
    name: "Yoga & Ayurveda",
    desc: "Rejuvenate with Himalayan yoga retreats and authentic Ayurvedic healing treatments.",
    icon: "Heart",
    count: "2 Packages",
    path: "/activities?category=Wellness",
    image: "https://upload.wikimedia.org/wikipedia/commons/f/fc/Yoga_Session-Wikicamp_Nepal_2018-Day_03-4590.jpg",
    color: "from-rose-500 to-rose-600",
  },
  {
    name: "Jungle Safari",
    desc: "Chitwan wildlife safaris — spot rhinos, tigers, and exotic birds in their natural habitat.",
    icon: "Binoculars",
    count: "3 Packages",
    path: "/activities/jungle-safari",
    image: "https://upload.wikimedia.org/wikipedia/commons/6/61/Elephant_Safari_inside_Chitwan_National_Park.jpg",
    color: "from-green-600 to-green-700",
  },
  {
    name: "Rafting",
    desc: "White-water rafting on Nepal's legendary rivers — Trishuli, Bhote Koshi, and Kali Gandaki.",
    icon: "Ship",
    count: "3 Packages",
    path: "/activities/rafting",
    image: "https://upload.wikimedia.org/wikipedia/commons/5/54/Trishuli_River_Rafting%2C_Nepal-3133.jpg",
    color: "from-sky-500 to-sky-600",
  },
  {
    name: "Heli Tours",
    desc: "Soar above Everest, land at Base Camp, and experience the Himalayas from the sky.",
    icon: "Helicopter",
    count: "2 Tours",
    path: "/activities?category=Heli+Tour",
    image: "https://upload.wikimedia.org/wikipedia/commons/5/5d/Air_Dynasty_Heli_Service%2C_Nepal.jpg",
    color: "from-blue-500 to-blue-600",
  },
  {
    name: "Festival Tours",
    desc: "Experience Dashain, Tihar, Holi, Loshar, and Nepal's vibrant cultural celebrations.",
    icon: "Music",
    count: "5 Festivals",
    path: "/activities?category=Festival+Tour",
    image: "https://upload.wikimedia.org/wikipedia/commons/9/9c/Holi_celebrations_in_Pokhara%2C_Nepal_March_2012.jpg",
    color: "from-pink-500 to-pink-600",
  },
];

const PopularPackages = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 lg:py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 text-accent text-sm font-semibold rounded-full mb-4">
            <Icon name="Zap" size={14} />
            Popular Packages
          </span>
          <h2 className="section-heading text-foreground mb-5">
            Activities in <span style={{ color: "#2d5016" }}>Nepal</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            From the highest peaks to the deepest jungles — discover every adventure Nepal has to offer.
          </p>
        </div>

        {/* Activity type icons row (like peakhimalaya.com) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mb-14">
          {PACKAGES.map((pkg, i) => (
            <button
              key={i}
              onClick={() => navigate(pkg.path)}
              className="group flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow`}>
                <Icon name={pkg.icon} size={20} color="white" />
              </div>
              <span className="text-xs font-semibold text-gray-700 text-center leading-tight">{pkg.name}</span>
            </button>
          ))}
        </div>

        {/* Package cards grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {PACKAGES.map((pkg, i) => (
            <div
              key={i}
              onClick={() => navigate(pkg.path)}
              className="group relative bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              </div>
              <div className="absolute top-3 right-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold text-gray-700 shadow-sm">
                  <Icon name="Package" size={11} />
                  {pkg.count}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${pkg.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={pkg.icon} size={13} color="white" />
                  </div>
                  <h3 className="font-headline font-semibold text-white text-sm">{pkg.name}</h3>
                </div>
                <p className="text-xs text-white/70 line-clamp-1">{pkg.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate("/activities")}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent text-white font-semibold rounded-xl hover:bg-accent/90 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Icon name="Grid3x3" size={16} />
            View All Activities
          </button>
        </div>
      </div>
    </section>
  );
};

export default PopularPackages;
